<script setup lang="ts">
import type { PrereqLine } from '@/types/constructible';

/**
 * Liste PARTAGÉE de prérequis (bâtiments / vaisseaux / recherche). Format
 * « Type : Label N{cur}/{req} » ; un prérequis sans niveau (ex. chantier naval)
 * n'affiche que le label. Pastille ✓ (rempli) / 🔒 (manquant).
 */
defineProps<{
  items: PrereqLine[];
  /** Titre optionnel (ex. « Prérequis manquants »). */
  title?: string;
}>();

const KIND_LABEL: Record<string, string> = {
  research: 'Recherche',
  building: 'Bâtiment',
  biome: 'Biome',
  ship: 'Vaisseau',
  Shipyard: 'Chantier',
};

function line(p: PrereqLine): string {
  const kind = KIND_LABEL[p.kind] ?? p.kind;
  // Sans niveau requis (chantier naval) -> libellé seul.
  if (p.requiredLevel === undefined || p.requiredLevel === null) return `${kind} : ${p.label}`;
  return `${kind} : ${p.label} N${p.currentLevel ?? 0}/${p.requiredLevel}`;
}
</script>

<template>
  <div v-if="items.length" class="pql">
    <span v-if="title" class="pql-title">{{ title }}</span>
    <ul class="pql-list">
      <li v-for="(p, i) in items" :key="i" :class="p.satisfied ? 'is-ok' : 'is-missing'">
        <span class="pql-mark" aria-hidden="true">{{ p.satisfied ? '✓' : '🔒' }}</span>
        {{ line(p) }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.pql { display: flex; flex-direction: column; gap: 3px; }
.pql-title {
  font-size: 0.72em; text-transform: uppercase; letter-spacing: 0.04em;
  opacity: 0.7; color: $color-danger;
}
.pql-list { list-style: none; margin: 0; padding: 0; font-size: 0.85em; display: flex; flex-direction: column; gap: 2px; }
.pql-list li { display: flex; align-items: center; gap: 5px; }
.pql-mark { flex-shrink: 0; font-size: 0.9em; }
.is-missing { color: $color-danger; }
.is-ok { color: $accent-positive; opacity: 0.85; }
</style>
